import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Bell, ArrowRight, Sparkles, Heart, Briefcase, Wallet, Users, Home, GraduationCap, Plane, Sunrise, Database } from 'lucide-react';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import Watermark from '../components/Watermark';
import { useSession } from '../context/SessionContext';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

const MyStoryTab = () => {
  const navigate = useNavigate();
  // ONLY use SessionContext - MongoDB is source of truth
  const { isPremium, isAuthenticated, refreshSubscription } = useSession();
  const [showUnlockPopup, setShowUnlockPopup] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  // isUnlocked comes ONLY from SessionContext (MongoDB)
  const isUnlocked = isPremium;
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Refresh subscription status from MongoDB on mount
  useEffect(() => {
    if (isAuthenticated) {
      refreshSubscription();
    }
  }, [isAuthenticated, refreshSubscription]);

  console.log('🔒 MyStoryTab - isPremium from MongoDB:', isPremium, 'isUnlocked:', isUnlocked);

  const domains = [
    { 
      id: 1, 
      name: 'Health & Longevity', 
      icon: '💚',
      emoji: '🫀',
      gradient: 'from-emerald-400 to-teal-500',
      angle: 0 // Top
    },
    { 
      id: 2, 
      name: 'Career & Profession', 
      icon: '💼',
      emoji: '🎯',
      gradient: 'from-blue-400 to-indigo-500',
      angle: 45 // Top-right
    },
    { 
      id: 3, 
      name: 'Wealth & Finances', 
      icon: '💰',
      emoji: '💎',
      gradient: 'from-amber-400 to-orange-500',
      angle: 90 // Right
    },
    { 
      id: 4, 
      name: 'Relationships & Marriage', 
      icon: '💗',
      emoji: '💑',
      gradient: 'from-pink-400 to-rose-500',
      angle: 135 // Bottom-right
    },
    { 
      id: 5, 
      name: 'Family & Children', 
      icon: '🏡',
      emoji: '👨‍👩‍👧',
      gradient: 'from-violet-400 to-purple-500',
      angle: 180 // Bottom
    },
    { 
      id: 6, 
      name: 'Education & Skills', 
      icon: '🎓',
      emoji: '📚',
      gradient: 'from-cyan-400 to-blue-500',
      angle: 225 // Bottom-left
    },
    { 
      id: 7, 
      name: 'Travel & Foreign', 
      icon: '✈️',
      emoji: '🌍',
      gradient: 'from-teal-400 to-green-500',
      angle: 270 // Left
    },
    { 
      id: 8, 
      name: 'Spirituality & Growth', 
      icon: '🌅',
      emoji: '🕉️',
      gradient: 'from-indigo-400 to-violet-500',
      angle: 315 // Top-left
    },
  ];

  const handleDomainClick = (domain) => {
    if (isUnlocked) {
      // If already unlocked, show the domain's future story
      toast.success(`Opening ${domain.name} future story...`);
      // Navigate to domain story page (to be implemented)
      navigate(`/domain-story/${domain.id}`);
      return;
    }
    // If not unlocked, navigate to payment page
    setSelectedDomain(domain);
    toast.info('Unlock to see your future predictions');
    navigate('/payment-plans');
  };

  const handleSubscribe = () => {
    setShowUnlockPopup(false);
    setShowPaymentModal(true);
  };

  const handlePayment = async (amount) => {
    setPaymentProcessing(true);
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mark as unlocked
      localStorage.setItem('story_unlocked', 'true');
      setShowPaymentModal(false);
      setShowSuccessPopup(true);
      
      toast.success('Payment successful! All domains unlocked!');
      
      // Hide success popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
      
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  return (
    <CosmicBackground showAstroChart={false}>
      
      {/* FULL WIDTH - NO PADDING - TRILLION DOLLAR DESIGN */}
      <div className="relative h-screen overflow-hidden flex flex-col">
        {/* Header with Full Chart button */}
        <div className="flex justify-end items-center pt-6 pb-3 px-4 z-30">
          
          <Link 
            to="/kernel"
            className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 px-6 py-3 rounded-full transition-all hover:scale-105 shadow-lg"
            title="View Complete Astrological Chart"
          >
            <Database className="w-5 h-5 text-white" />
            <span className="text-white font-medium">View Full Chart</span>
          </Link>
        </div>

        {/* CONTENT AREA - Shows different content based on unlock status */}
        <div className="flex-1 relative overflow-hidden">
          
          {!isUnlocked ? (
            // LOCKED STATE - Show Orbital Layout
            <div className="relative h-full flex items-center justify-center px-8">
              <div className="relative w-full h-full flex items-center justify-center" style={{ transform: 'translateY(-40px)' }}>
                <div className="relative" style={{ width: '260px', height: '260px' }}>
                  
                  {/* Sage - EXACT CENTER */}
                  <div className="absolute z-10 pointer-events-none" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
                    <img 
                      src="/sage-final.png"
                      alt="Sage"
                      className="w-40 h-56 object-contain"
                      style={{ 
                        filter: 'brightness(1.3) contrast(1.15) saturate(1.2) drop-shadow(0 0 20px rgba(212,175,55,0.6))',
                        mixBlendMode: 'normal'
                      }}
                    />
                  </div>

              {/* 8 Domains - OPTIMIZED for mobile app */}
              {domains.map((domain) => {
                const radius = 90; // Bigger radius for mobile app view
                const angleRad = ((domain.angle - 90) * Math.PI) / 180;
                const x = Math.cos(angleRad) * radius + 130;
                const y = Math.sin(angleRad) * radius + 130;
              
              return (
                <button
                  key={domain.id}
                  onClick={() => handleDomainClick(domain)}
                  onMouseEnter={() => setHoveredCard(domain.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className="absolute group animate-unfold"
                  style={{
                    left: `${x}px`,
                    top: `${y}px`,
                    transform: 'translate(-50%, -50%)',
                    '--target-x': `${x - 150}px`,
                    '--target-y': `${y - 150}px`,
                    animationDelay: `${domain.id * 0.12}s`
                  }}
                >
                  {/* Card - BIGGER for mobile app */}
                  <div 
                    className={`relative w-[70px] h-[70px] rounded-2xl bg-gradient-to-br ${domain.gradient} backdrop-blur-2xl border-2 border-white/50 overflow-hidden transition-all duration-500 animate-gentle-bounce ${hoveredCard === domain.id ? 'scale-[1.3] shadow-[0_0_60px_rgba(255,255,255,0.5)] rotate-[8deg]' : 'scale-100 shadow-2xl rotate-0'}`}
                    style={{
                      animationDelay: `${1.5 + domain.id * 0.12}s`,
                      animationDuration: `${3 + (domain.id * 0.1)}s`
                    }}
                  >
                      
                      {/* Animated shimmer sweep */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 animate-shimmer" />
                      
                      {/* Pulsing gradient layer */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${domain.gradient} opacity-25 animate-pulse-slow`} />
                      
                      {/* Beautiful Emoji Icon - BIGGER for app */}
                      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${hoveredCard === domain.id ? 'scale-125 rotate-12' : 'scale-100 rotate-0'}`}>
                        <span className="text-[48px] drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)] filter brightness-110">
                          {domain.icon}
                        </span>
                      </div>
                      
                      {/* Animated Lock Badge - Only show if locked */}
                      {!isUnlocked && (
                        <div className="absolute top-1.5 right-1.5">
                          <div className={`w-6 h-6 rounded-lg bg-black/80 backdrop-blur-sm flex items-center justify-center border transition-all duration-300 ${hoveredCard === domain.id ? 'border-[#D4AF37] shadow-[0_0_15px_rgba(212,175,55,0.8)]' : 'border-white/30'}`}>
                            <Lock className={`w-3.5 h-3.5 transition-colors duration-300 ${hoveredCard === domain.id ? 'text-[#D4AF37]' : 'text-white'}`} strokeWidth={2.5} />
                          </div>
                        </div>
                      )}
                      
                      {/* Unlocked Checkmark Badge */}
                      {isUnlocked && (
                        <div className="absolute top-1.5 right-1.5">
                          <div className="w-6 h-6 rounded-lg bg-cyan-500/90 backdrop-blur-sm flex items-center justify-center border border-cyan-300 shadow-[0_0_15px_rgba(34,211,238,0.6)]">
                            <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                          </div>
                        </div>
                      )}
                      
                      {/* Massive orbital glow */}
                      <div className={`absolute -inset-3 bg-gradient-to-br ${domain.gradient} opacity-50 blur-2xl -z-10 transition-all duration-500 ${hoveredCard === domain.id ? 'opacity-80 blur-3xl scale-125' : 'opacity-50'}`} />
                      
                      {/* Corner sparkle */}
                      <div className={`absolute top-0 right-0 w-10 h-10 bg-white/30 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    </div>
                    
                {/* Domain Label */}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 transition-all duration-300 ${hoveredCard === domain.id ? 'opacity-100 translate-y-1' : 'opacity-80 translate-y-0'}`} style={{ maxWidth: '85px' }}>
                  <div className={`text-white text-[8px] font-medium tracking-wide text-center px-2 py-0.5 bg-gradient-to-r ${domain.gradient} bg-opacity-30 backdrop-blur-xl rounded-full border border-white/30 shadow-xl leading-tight`}>
                    {domain.name}
                  </div>
                </div>
              </button>
            );
          })}
            
                </div>
              </div>
            </div>
          ) : (
            // UNLOCKED STATE - Show Sage Vaarta Videos (Horizontal Scroll)
            <div className="relative h-full">
              <div className="px-6 py-4">
                <h2 className="text-white text-lg font-light tracking-wide mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-cyan-400" />
                  Sage Vaarta - Your Divine Guidance
                </h2>
              </div>
              
              {/* Horizontal Scrolling Video Cards - BIGGER for mobile app */}
              <div className="overflow-x-auto hide-scrollbar px-6 pb-4">
                <div className="flex gap-6" style={{ width: 'max-content' }}>
                  {domains.map((domain, index) => (
                    <div 
                      key={domain.id}
                      className="relative w-[320px] h-[480px] rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border-2 border-white/20 flex-shrink-0 shadow-2xl"
                    >
                      {/* Video/Image Background */}
                      <div className="absolute inset-0">
                        <div className={`w-full h-full bg-gradient-to-br ${domain.gradient} opacity-20`} />
                        {/* Placeholder for video - will add actual video later */}
                        <img 
                          src="/sage-final.png"
                          alt={`${domain.name} Sage`}
                          className="absolute inset-0 w-full h-full object-cover opacity-40"
                          style={{
                            filter: 'brightness(0.8) contrast(1.1)',
                            mixBlendMode: 'overlay'
                          }}
                        />
                      </div>
                      
                      {/* Content Overlay */}
                      <div className="relative z-10 h-full flex flex-col justify-between p-6">
                        {/* Top - Domain Info - BIGGER */}
                        <div>
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${domain.gradient} flex items-center justify-center text-3xl shadow-xl border-2 border-white/30`}>
                              {domain.icon}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-white font-semibold text-base">{domain.name}</h3>
                              <p className="text-cyan-400 text-sm">Sage Vaarta {index + 1}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Center - Large Sage Image - BIGGER */}
                        <div className="flex-1 flex items-center justify-center py-4">
                          <div className="relative w-40 h-40">
                            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-xl animate-pulse" />
                            <img 
                              src="/sage-final.png"
                              alt="Sage"
                              className="relative w-full h-full object-contain drop-shadow-2xl"
                              style={{filter: 'brightness(1.2) contrast(1.1)'}}
                            />
                          </div>
                        </div>
                        
                        {/* Bottom - Play Button - BIGGER */}
                        <div className="flex items-center justify-center">
                          <button 
                            onClick={() => {
                              // Navigate to the reels-style story page starting from this domain
                              toast.success(`${domain.icon} ${domain.name}`);
                              navigate(`/future-story/${domain.id}`);  // Use new unified future story
                            }}
                            className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform border-4 border-white/30"
                          >
                            <div className="w-0 h-0 border-t-[12px] border-t-transparent border-l-[20px] border-l-white border-b-[12px] border-b-transparent ml-1" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Swipe Indicator */}
              <div className="text-center py-2">
                <p className="text-white/30 text-xs flex items-center justify-center gap-2">
                  <ArrowRight className="w-3 h-3" />
                  Swipe to explore all domains
                  <ArrowRight className="w-3 h-3" />
                </p>
              </div>
            </div>
          )}
          
        </div>

        {/* Greeting at Bottom */}
        <div className="text-center pb-6 z-30 px-4">
          <h1 className="text-white text-xl font-light tracking-wide">Namaskar Rahul,</h1>
          <p className="text-white/40 text-xs mt-1">Your cosmic journey awaits</p>
        </div>

        {/* Unlock Popup - ₹999/Month */}
        {showUnlockPopup && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-lg z-50 flex items-center justify-center p-6">
            <div className="relative bg-gradient-to-br from-[#0a1628]/95 to-[#1a3a5a]/95 backdrop-blur-2xl rounded-3xl p-8 max-w-sm w-full border border-cyan-500/20 shadow-2xl">
              
              {/* Large Lock Icon with Blue Glow */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  {/* Cyan glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 blur-3xl opacity-40 rounded-full scale-150" />
                  
                  {/* Main lock container */}
                  <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-600/20 backdrop-blur-sm flex items-center justify-center border-2 border-cyan-400/40">
                    <Lock className="w-12 h-12 text-cyan-400" strokeWidth={1.5} />
                  </div>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-white text-2xl font-light text-center mb-2 tracking-widest">
                UNLOCK ALL DOMAINS
              </h2>

              {/* Subscription Badge */}
              <div className="flex justify-center mb-6">
                <div className="px-4 py-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full text-white text-xs font-medium">
                  MONTHLY SUBSCRIPTION
                </div>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                  ₹999
                </div>
                <div className="text-white/50 text-sm mt-1">per month</div>
              </div>

              {/* Features */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-3 text-white/80 text-sm">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-cyan-400" />
                  </div>
                  <span>Access to all 8 life domains</span>
                </div>
                <div className="flex items-center gap-3 text-white/80 text-sm">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-cyan-400" />
                  </div>
                  <span>Sage Vaarta videos for each domain</span>
                </div>
                <div className="flex items-center gap-3 text-white/80 text-sm">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-cyan-400" />
                  </div>
                  <span>Detailed predictions & insights</span>
                </div>
                <div className="flex items-center gap-3 text-white/80 text-sm">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-cyan-400" />
                  </div>
                  <span>Monthly calendar & reminders</span>
                </div>
              </div>

              {/* Subscribe Button */}
              <button 
                onClick={handleSubscribe}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 text-white rounded-full font-semibold text-lg flex items-center justify-center gap-2 shadow-2xl hover:scale-105 hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] transition-all"
              >
                Subscribe Now
                <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
              </button>

              {/* Go Back Button */}
              <button 
                onClick={() => setShowUnlockPopup(false)}
                className="w-full mt-5 py-3 text-white/50 text-sm font-light hover:text-white transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-lg z-50 flex items-center justify-center p-6">
            <div className="relative bg-gradient-to-br from-[#0a1628]/95 to-[#1a3a5a]/95 backdrop-blur-2xl rounded-3xl p-8 max-w-md w-full border border-[#D4AF37]/20 shadow-2xl">
              {/* Close Button */}
              <button
                onClick={() => setShowPaymentModal(false)}
                className="absolute top-4 right-4 text-white/50 hover:text-white"
              >
                ✕
              </button>

              <h2 className="text-white text-2xl font-bold text-center mb-6">
                Choose Your Plan
              </h2>

              {/* 3 Payment Plans */}
              <div className="space-y-4 mb-6">
                {/* Plan 1: One-time */}
                <button
                  onClick={() => handlePayment(1000)}
                  disabled={paymentProcessing}
                  className="w-full p-4 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl text-white hover:scale-105 transition-transform disabled:opacity-50"
                >
                  <div className="flex justify-between items-center">
                    <div className="text-left">
                      <p className="font-bold">One-Time Access</p>
                      <p className="text-sm opacity-90">Lifetime access</p>
                    </div>
                    <p className="text-2xl font-bold">₹1000</p>
                  </div>
                </button>

                {/* Plan 2: Monthly */}
                <button
                  onClick={() => handlePayment(1000)}
                  disabled={paymentProcessing}
                  className="w-full p-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl text-white hover:scale-105 transition-transform disabled:opacity-50"
                >
                  <div className="flex justify-between items-center">
                    <div className="text-left">
                      <p className="font-bold">Monthly Subscription</p>
                      <p className="text-sm opacity-90">Cancel anytime</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">₹1000</p>
                      <p className="text-xs">/month</p>
                    </div>
                  </div>
                </button>

                {/* Plan 3: Premium */}
                <button
                  onClick={() => handlePayment(2999)}
                  disabled={paymentProcessing}
                  className="w-full p-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white hover:scale-105 transition-transform disabled:opacity-50 relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 bg-yellow-400 text-black text-xs px-2 py-1 rounded-bl-lg font-bold">
                    BEST VALUE
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-left">
                      <p className="font-bold">Premium Mastery</p>
                      <p className="text-sm opacity-90">Advanced features</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">₹2999</p>
                      <p className="text-xs">/month</p>
                    </div>
                  </div>
                </button>
              </div>

              {paymentProcessing && (
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#D4AF37] border-t-transparent"></div>
                  <p className="text-white mt-2">Processing payment...</p>
                </div>
              )}

              <p className="text-gray-400 text-xs text-center mt-4">
                Powered by Razorpay
              </p>
            </div>
          </div>
        )}

        {/* Success Popup */}
        {showSuccessPopup && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-lg z-50 flex items-center justify-center p-6">
            <div className="relative bg-gradient-to-br from-[#0a1628]/95 to-[#1a3a5a]/95 backdrop-blur-2xl rounded-3xl p-8 max-w-sm w-full border border-cyan-500/20 shadow-2xl animate-fadeIn">
              {/* Success Icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 blur-3xl opacity-60 rounded-full scale-150 animate-pulse" />
                  <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                    <Sparkles className="w-12 h-12 text-white" strokeWidth={2} />
                  </div>
                </div>
              </div>

              <h2 className="text-white text-2xl font-light text-center mb-3 tracking-widest">
                WELCOME!
              </h2>
              <p className="text-cyan-400 text-center text-base mb-4">
                All domains unlocked successfully
              </p>
              <p className="text-white/50 text-center text-sm">
                Explore your personalized Sage Vaarta and insights
              </p>
            </div>
          </div>
        )}

        {/* Bottom Sparkle Icon */}
        <div className="fixed bottom-28 right-8 z-20">
          <Sparkles className="w-10 h-10 text-white/30 animate-pulse" />
        </div>
      </div>
    </CosmicBackground>
  );
};

export default MyStoryTab;